import type { TransactionDetail } from '../schemas/transactionDetail'
import {
  type AccountInfo,
  getAccountInfo as getAccountInfoByNode,
} from './getAccountInfo'
import {
  type Signature,
  getSignaturesForAddress as getSignaturesForAddressByNode,
} from './getSignaturesForAddress'
import {
  type TokenAccount,
  getTokenAccountsByOwner as getTokenAccountsByOwnerByNode,
} from './getTokenAccountsByOwner'
import { getTransaction as getTransactionByNode } from './getTransaction'

/**
 * Headers can be provided as:
 * - Static object: Record<string, string>
 * - Async function: () => Promise<Record<string, string> | undefined>
 * - undefined: No headers
 */
export type HeadersInput =
  | Record<string, string>
  | (() => Promise<Record<string, string> | undefined>)
  | undefined

/**
 * Resolves HeadersInput to actual headers object.
 * If headers is a function, it will be called to get the headers.
 * This allows for dynamic header generation (e.g., refreshing auth tokens).
 */
const resolveHeaders = async (
  headers: HeadersInput,
): Promise<Record<string, string> | undefined> => {
  if (headers === undefined) {
    return undefined
  }
  if (typeof headers === 'function') {
    return await headers()
  }
  return headers
}

class Semaphore {
  #endpoints: readonly string[]
  #usingIndexSet = new Set<number>()
  #queue: Array<{
    call: () => void
    timeoutId: ReturnType<typeof setTimeout>
  }> = []

  constructor(endpoints: readonly string[]) {
    this.#endpoints = endpoints
  }

  async acquire(timeout: number): Promise<string> {
    if (this.#usingIndexSet.size < this.#endpoints.length) {
      const index = this.#endpoints.findIndex(
        (_, index) => !this.#usingIndexSet.has(index),
      )
      this.#usingIndexSet.add(index)
      const endpoint = this.#endpoints[index]
      return Promise.resolve(endpoint)
    }

    return new Promise<string>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        const index = this.#queue.findIndex(
          (item) => item.timeoutId === timeoutId,
        )
        if (index !== -1) {
          this.#queue.splice(index, 1)
          reject(new Error('Timeout while waiting for semaphore'))
        }
      }, timeout)

      this.#queue.push({
        call: () => {
          const index = this.#endpoints.findIndex(
            (_, index) => !this.#usingIndexSet.has(index),
          )
          this.#usingIndexSet.add(index)
          const endpoint = this.#endpoints[index]
          resolve(endpoint)
        },
        timeoutId,
      })
    })
  }

  async release(endpoint: string, cooldown?: number): Promise<void> {
    if (cooldown !== undefined) {
      await new Promise((resolve) => setTimeout(resolve, cooldown))
    }
    const index = this.#endpoints.indexOf(endpoint)
    this.#usingIndexSet.delete(index)
    const next = this.#queue.shift()
    if (next !== undefined) {
      const { call, timeoutId } = next
      clearTimeout(timeoutId)
      call()
    }
  }
}

export type ClientCallParameters = {
  endpoint: string
  headers?: Record<string, string>
}

export type Client = {
  rpcCount: number
  getAccountInfo: (params: {
    address: string
    headers?: HeadersInput
    timeout?: number
  }) => Promise<AccountInfo | undefined>
  getSignaturesForAddress: (params: {
    address: string
    before?: string
    until?: string
    headers?: HeadersInput
    timeout?: number
  }) => Promise<readonly Signature[]>
  getTokenAccountsByOwner: (params: {
    ownerPubKey: string
    headers?: HeadersInput
    timeout?: number
  }) => Promise<readonly TokenAccount[]>
  getTransaction: (params: {
    signature: string
    headers?: HeadersInput
    timeout?: number
  }) => Promise<TransactionDetail | undefined>
}

export const createSolanaClient = (params: {
  rpcEndpoints: readonly string[]
}): Client => {
  const { rpcEndpoints } = params
  const semaphore = new Semaphore(rpcEndpoints)

  const call = async <T>({
    clientCall,
    callName,
    headers,
    timeout = 60_000,
  }: {
    clientCall: (params: ClientCallParameters) => Promise<T>
    callName: string
    headers?: HeadersInput
    timeout?: number
  }) => {
    const start = Date.now()
    const endpoint = await semaphore.acquire(timeout)
    let cooldown: number | undefined
    console.debug(`Call ${callName} on ${endpoint}`)
    try {
      // Resolve headers on each call to support dynamic token refresh
      const resolvedHeaders = await resolveHeaders(headers)
      return await Promise.race([
        clientCall({ endpoint, headers: resolvedHeaders }),
        new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error('Timeout'))
          }, 15_000)
        }),
      ])
    } catch (error) {
      if (error instanceof Error && error.message.includes('429')) {
        cooldown = 60_000
        // Pass original headers (not resolved) so it can be re-resolved on retry
        return await call({
          clientCall,
          callName,
          headers,
          timeout: timeout - (Date.now() - start),
        })
      }
      throw error
    } finally {
      semaphore.release(endpoint, cooldown)
    }
  }

  return {
    rpcCount: rpcEndpoints.length,
    /** {@link https://solana.com/docs/rpc/http/getaccountinfo} */
    getAccountInfo: async ({
      address,
      headers,
      timeout,
    }: {
      address: string
      headers?: HeadersInput
      timeout?: number
    }) =>
      call({
        clientCall: (params) => getAccountInfoByNode({ ...params, address }),
        callName: 'getAccountInfo',
        headers,
        timeout,
      }),
    /** {@link https://solana.com/docs/rpc/http/getsignaturesforaddress} */
    getSignaturesForAddress: async ({
      address,
      before,
      until,
      headers,
      timeout,
    }: {
      address: string
      before?: string
      until?: string
      headers?: HeadersInput
      timeout?: number
    }) =>
      call({
        clientCall: (params) =>
          getSignaturesForAddressByNode({ ...params, address, before, until }),
        callName: 'getSignaturesForAddress',
        headers,
        timeout,
      }),
    /** {@link https://solana.com/docs/rpc/http/gettokenaccountsbyowner} */
    getTokenAccountsByOwner: async ({
      ownerPubKey,
      headers,
      timeout,
    }: {
      ownerPubKey: string
      headers?: HeadersInput
      timeout?: number
    }) =>
      call({
        clientCall: (params) =>
          getTokenAccountsByOwnerByNode({ ...params, ownerPubKey }),
        callName: 'getTokenAccountsByOwner',
        headers,
        timeout,
      }),
    /** {@link https://solana.com/docs/rpc/http/gettransaction} */
    getTransaction: async ({
      signature,
      headers,
      timeout,
    }: {
      signature: string
      headers?: HeadersInput
      timeout?: number
    }) =>
      call({
        clientCall: (params) => getTransactionByNode({ ...params, signature }),
        callName: 'getTransaction',
        headers,
        timeout,
      }),
  }
}
